/**
 * Naming and colour for support statuses, priorities and categories. Separate
 * from the badge components so routes can import the maps without tripping
 * react-refresh's one-kind-of-export rule.
 */

import type {
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketStatus,
} from '@repo/types';

/** Staff see the raw lifecycle vocabulary; the customer surface softens it. */
export const SUPPORT_STATUS_PRESENTATION: Record<
  SupportTicketStatus,
  { label: string; className: string }
> = {
  open: {
    label: 'Open',
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/25 dark:text-amber-500',
  },
  in_progress: {
    label: 'In progress',
    className: 'bg-primary/10 text-chart-1 border-primary/25',
  },
  waiting_on_customer: {
    label: 'Waiting on customer',
    className: 'bg-muted text-muted-foreground border-border',
  },
  resolved: {
    label: 'Resolved',
    className: 'bg-emerald-600/10 text-emerald-600 border-emerald-600/25 dark:text-emerald-500',
  },
  closed: {
    label: 'Closed',
    className: 'bg-muted text-muted-foreground border-border',
  },
};

/** Only high and urgent are coloured, or nothing stands out. */
export const SUPPORT_PRIORITY_PRESENTATION: Record<
  SupportTicketPriority,
  { label: string; className: string }
> = {
  low: { label: 'Low', className: 'bg-muted text-muted-foreground border-border' },
  normal: { label: 'Normal', className: 'bg-muted text-muted-foreground border-border' },
  high: {
    label: 'High',
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-500',
  },
  urgent: {
    label: 'Urgent',
    className: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

export const SUPPORT_CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  account: 'Account',
  billing: 'Billing',
  jobs_matching: 'Job matches',
  auto_apply: 'Auto apply',
  resume: 'Resume',
  technical: 'Technical',
  other: 'Other',
};

/** Readable stand-in for a value shipped by the backend before the UI knew it. */
export function supportCategoryLabel(category: SupportTicketCategory): string {
  const known = SUPPORT_CATEGORY_LABELS[category];
  if (known) return known;
  const spaced = String(category).replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const SUPPORT_STATUS_LABELS = Object.fromEntries(
  Object.entries(SUPPORT_STATUS_PRESENTATION).map(([key, value]) => [key, value.label])
) as Record<SupportTicketStatus, string>;

export const SUPPORT_PRIORITY_LABELS = Object.fromEntries(
  Object.entries(SUPPORT_PRIORITY_PRESENTATION).map(([key, value]) => [key, value.label])
) as Record<SupportTicketPriority, string>;
